import { createListCollection, useAsyncList } from '@ark-ui/react/collection';
import { Combobox, useCombobox } from '@ark-ui/react/combobox';
import { Portal } from '@ark-ui/react/portal';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { FocusEvent, startTransition } from 'react';

import { useDictionary } from 'src/hooks/useDictionary';
import { useSite } from 'src/hooks/useSite';
import { useSiteSearchSubmitContext } from 'src/hooks/useSiteSearchSubmitContext';

import { formatObjectToQueryString } from 'src/utils/formatObjectToQueryString';
import {
  addQueryToRecentSearches,
  deleteQueryFromRecentSearches,
  getRecentSearchQueries,
} from 'src/utils/recentSearchesFromLocalStorage';

import { CoreGlobalNavigationHeaderData } from 'src/types/components';
import { TypeaheadSearchResult } from 'src/types/model';

import ArrowForward from 'assets/icons/arrow-forward.svg';
import CloseIcon from 'assets/icons/close.svg';
import MagnifyingGlass from 'assets/icons/magnifying-glass.svg';

import Text from 'components/BaseHelpers/Text';

import { getTypeaheadResultsFactory } from './getTypeaheadResult';
import { resolveSiteSearchSelection } from './siteSearchSelection';

import styles from './SearchInput.module.scss';

export type SearchInputProps = CoreGlobalNavigationHeaderData['fields']['search'] & {
  className?: string;
};

export const localStorageKey = 'siteSearchRecentSearchQueries';

const SearchInput = (props: SearchInputProps): JSX.Element => {
  const { placeholder, typeaheadResultsCount, recentSearchesLabel, className } = props;
  const dictionary = useDictionary();
  const router = useRouter();
  const { site } = useSite();
  const { submitSiteSearchQuery } = useSiteSearchSubmitContext();

  const list = useAsyncList<TypeaheadSearchResult>({
    async load({ filterText, signal }) {
      if (signal?.aborted) return { items: [] };

      if (filterText === localStorageKey) {
        return {
          items: getRecentSearchQueries(localStorageKey),
        };
      }

      if (filterText.length < 3) {
        return { items: [] };
      }

      const getTypeaheadResult = getTypeaheadResultsFactory('/api/site-search/typeahead', site);
      const data = await getTypeaheadResult(filterText, typeaheadResultsCount);

      return { items: data.results };
    },
  });

  const collection = createListCollection({
    items: list.items,
    itemToString: (item) => item.title,
    itemToValue: (item) => item.value,
  });

  const handleInputChange = (details: Combobox.InputValueChangeDetails) => {
    if (details.reason === 'input-change') {
      startTransition(() => {
        list.setFilterText(details.inputValue);
      });
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (!event.currentTarget.value) {
      startTransition(() => {
        list.setFilterText(localStorageKey);
      });
    }
  };

  const isRecentSearches = list.filterText === localStorageKey;

  const combobox = useCombobox({
    allowCustomValue: true,
    collection,
    onInputValueChange: handleInputChange,
    selectionBehavior: 'preserve',
    openOnClick: true,
    closeOnSelect: true,
    onSelect: (details) => {
      const selection = resolveSiteSearchSelection(details.itemValue);

      if (selection?.type === 'query') {
        navigateToSiteSearchResults(selection.query);
        return;
      }

      if (selection?.type === 'link') {
        router.push(selection.href);
        return;
      }

      router.push(details.itemValue);
    },
  });

  const navigateToSiteSearchResults = (query: string) => {
    const normalizedQuery = query.trim();

    if (!normalizedQuery) {
      return;
    }

    const searchUrl = `/search?${formatObjectToQueryString({ query: normalizedQuery })}`;
    const currentPath = router.asPath.split('?')[0].split('#')[0];
    const handledByMountedSiteSearch =
      currentPath === '/search' && submitSiteSearchQuery(normalizedQuery);

    if (!handledByMountedSiteSearch) {
      router.push(searchUrl);
    }

    addQueryToRecentSearches({ title: normalizedQuery, value: searchUrl }, localStorageKey);
    combobox.setOpen(false);
    combobox.clearValue();
  };

  return (
    <div className={cn(styles.main, className)}>
      <Combobox.RootProvider value={combobox}>
        <Combobox.Control className={styles.control}>
          <Combobox.Label aria-label="Search">
            <MagnifyingGlass className={styles['magnifying-glass']} />
          </Combobox.Label>
          <Combobox.Input
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                const query = event.currentTarget.value;

                if (query) {
                  navigateToSiteSearchResults(query);
                }
              }
            }}
            placeholder={placeholder}
            className={styles.input}
            onFocus={handleFocus}
          />
          {list.filterText.length > 0 && (
            <button
              className={styles['clear-button']}
              aria-label="Clear input"
              onClick={() => combobox.clearValue()}
            >
              <CloseIcon className={styles['close-icon']} />
            </button>
          )}
          <button
            className={styles.trigger}
            aria-label="Search"
            onClick={() => navigateToSiteSearchResults(combobox.inputValue)}
          >
            <ArrowForward className={styles['arrow-icon']} />
          </button>
        </Combobox.Control>
        <Portal>
          <Combobox.Positioner style={{ zIndex: 'var(--z-index__global-header-search-input)' }}>
            {collection.items.length > 0 && (
              <Combobox.Content className={styles.content}>
                {isRecentSearches && collection.items.length > 0 && (
                  <Text
                    className={cn(styles['recent-searches-label'], 'body-eyebrow')}
                    field={recentSearchesLabel}
                  />
                )}
                {collection.items.map((result) => (
                  <div className={styles['item-wrapper']} key={result.value}>
                    <Combobox.Item className={styles.item} item={result}>
                      <Combobox.ItemText className={cn(styles['item-text'], 'body-sm')}>
                        {result.title}
                      </Combobox.ItemText>
                    </Combobox.Item>
                    {isRecentSearches && (
                      <button
                        className={cn(styles['delete-button'], 'body-eyebrow')}
                        tabIndex={-1}
                        onClick={() => {
                          deleteQueryFromRecentSearches(result, localStorageKey);
                          list.reload();
                        }}
                      >
                        {dictionary.deleteLabel}
                      </button>
                    )}
                  </div>
                ))}
              </Combobox.Content>
            )}
          </Combobox.Positioner>
        </Portal>
      </Combobox.RootProvider>
    </div>
  );
};

export default SearchInput;
